/**
 * @file 购买增量包弹窗
 */

import React, {type FC, useEffect, useMemo, useState} from 'react';
import {Button, Modal} from 'antd';
import cx from 'classnames';

import BillingSparkSvg from '@/assets/billing/starm.svg';
import RequiredSparkSvg from '@/assets/billing/requiredSpark.svg';
import QuantityMinusSvg from '@/assets/billing/quantityMinus.svg';
import QuantityPlusSvg from '@/assets/billing/quantityPlus.svg';
import LinkSvg from '@/assets/icons/link.svg';
import type {IPointsPackagePlanView} from '@/stores/BillingStore';
import {formatNumberWithCommas} from '@/utils';
import styles from './index.module.less';

interface IPurchasePointsModalProps {
    open: boolean;
    loading: boolean;
    submitting: boolean;
    plans: IPointsPackagePlanView[];
    onCancel: () => void;
    onContactUs: () => void;
    onLoadPlans: () => Promise<void>;
    onPurchase: (id: string, quantity: number) => Promise<void>;
}

const MIN_QUANTITY = 1;
const MAX_QUANTITY = 5;
const DEFAULT_QUANTITY = 1;

const clampQuantity = (value: number) => Math.max(MIN_QUANTITY, Math.min(MAX_QUANTITY, value));

const PurchasePointsModal: FC<IPurchasePointsModalProps> = props => {
    const [selectedId, setSelectedId] = useState<string>('');
    const [quantity, setQuantity] = useState(DEFAULT_QUANTITY);

    useEffect(
        () => {
            if (!props.open) {
                return;
            }

            setQuantity(DEFAULT_QUANTITY);
            props.onLoadPlans().catch(() => undefined);
        },
        [props.open, props.onLoadPlans]
    );

    useEffect(
        () => {
            if (props.plans.length === 0) {
                setSelectedId('');
                return;
            }

            const hasSelectedPlan = props.plans.some(plan => plan.id === selectedId);
            if (!hasSelectedPlan) {
                setSelectedId(props.plans[0].id);
            }
        },
        [props.plans, selectedId]
    );

    const selectedPlan = useMemo(
        () => props.plans.find(plan => plan.id === selectedId),
        [props.plans, selectedId]
    );

    const handleContactUs = () => {
        props.onCancel();
        props.onContactUs();
    };

    const updateQuantity = (nextValue: number) => setQuantity(clampQuantity(nextValue));

    const handleQuantityChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const inputValue = event.target.value.trim();
        if (!inputValue) {
            setQuantity(MIN_QUANTITY);
            return;
        }

        const parsedValue = Number.parseInt(inputValue.replace(/\D/g, ''), 10);
        if (Number.isNaN(parsedValue)) {
            setQuantity(MIN_QUANTITY);
            return;
        }

        updateQuantity(parsedValue);
    };

    const handleQuantityKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'ArrowUp') {
            event.preventDefault();
            updateQuantity(quantity + 1);
            return;
        }

        if (event.key === 'ArrowDown') {
            event.preventDefault();
            updateQuantity(quantity - 1);
        }
    };

    const handlePurchase = async () => {
        if (!selectedId) {
            return;
        }

        await props.onPurchase(selectedId, quantity);
    };

    return (
        <Modal
            centered
            width={612}
            open={props.open}
            onCancel={props.onCancel}
            footer={null}
            className={styles.purchaseModal}
            title="购买增量包"
            destroyOnHidden
            keyboard={false} // 防止按ESC键关闭buyCountLabel
        >
            <div className={styles.packageGrid}>
                {props.plans.map(plan => (
                    <button
                        key={plan.id}
                        type="button"
                        className={cx(styles.packageCard, {[styles.activePackageCard]: selectedId === plan.id})}
                        onClick={() => setSelectedId(plan.id)}
                    >
                        <div className={styles.pointsTitle}>
                            <BillingSparkSvg />
                            <span>{formatNumberWithCommas(plan.points)} 资源点</span>
                        </div>
                        <div className={styles.validityText}>有效期{plan.validityDays}天</div>
                        <div className={styles.separator} />
                        <div className={styles.priceText}>{plan.monthlyPrice}</div>
                        <div className={styles.packagePriceText}>
                            <span className={styles.packagePriceSymbol}>¥</span>
                            {plan.packagePrice}
                        </div>
                    </button>
                ))}
            </div>

            <div className={styles.buyCountLabel}>
                <span>购买数量</span>
                <RequiredSparkSvg />
            </div>
            <div className={styles.quantityRow}>
                <div className={styles.quantityInputWrap}>
                    <button
                        type="button"
                        className={styles.quantityActionBtn}
                        onClick={() => updateQuantity(quantity - 1)}
                        disabled={quantity <= MIN_QUANTITY}
                        aria-label="减少购买数量"
                    >
                        <QuantityMinusSvg />
                    </button>
                    <input
                        type="text"
                        inputMode="numeric"
                        value={quantity}
                        onChange={handleQuantityChange}
                        onKeyDown={handleQuantityKeyDown}
                        className={styles.quantityValueInput}
                        aria-label="购买数量"
                    />
                    <button
                        type="button"
                        className={styles.quantityActionBtn}
                        onClick={() => updateQuantity(quantity + 1)}
                        disabled={quantity >= MAX_QUANTITY}
                        aria-label="增加购买数量"
                    >
                        <QuantityPlusSvg />
                    </button>
                </div>
            </div>

            <Button
                type="primary"
                block
                loading={props.submitting}
                disabled={props.loading || !selectedPlan}
                className={styles.purchaseBtn}
                onClick={handlePurchase}
            >
                立即购买
            </Button>

            <div className={styles.moreRow}>
                <span className={styles.moreText}>更多资源点购买</span>
                <button type="button" className={styles.contactBtn} onClick={handleContactUs}>
                    请联系我们
                    <LinkSvg />
                </button>
            </div>
        </Modal>
    );
};

export default PurchasePointsModal;
